/**
 * @author Fernando Ruano
 * @file saga.js
 * @description BloodPressure Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import bloodPressureService from '@nephro/services/bloodPressure-service';

// Constants
import {
  CREATE_BLOOD_PRESSURE,
  DELETE_BLOOD_PRESSURE_BY_ID,
  GET_ALL_BLOOD_PRESSURES,
  GET_BLOOD_PRESSURE_BY_ID,
  UPDATE_BLOOD_PRESSURE,
  GET_BLOOD_PRESSURE_BY_PATIENT_ID
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get bloodPressures from backend
 * @param { object } action - Action with payload data
 */
export function* getBloodPressuresArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      bloodPressureService.getBloodPressures,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get bloodPressure info by id
 * @param { object } action - Action with payload data
 */
export function* getBloodPressureById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(bloodPressureService.getBloodPressureById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.bloodPressureNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get bloodPressure info by patient id
 * @param { object } action - Action with payload data
 */
export function* getBloodPressureByPatientId(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      bloodPressureService.getBloodPressureByPatientId,
      id
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
    yield put(setLoading(false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.bloodPressureNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create bloodPressure
 * @param { object } action - Action with payload data
 */
export function* createBloodPressure(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  try {
    const response = yield call(bloodPressureService.createBloodPressure, data);
    yield put(
      setResponse(
        'success',
        'new bloodPressure',
        'patientRecords.recordCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update bloodPressure
 * @param { object } action - Action with payload data
 */
export function* updateBloodPressure(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      bloodPressureService.updateBloodPressure,
      data,
      id
    );
    yield put(
      setResponse(
        'success',
        'updated bloodPressure',
        'patientRecords.recordUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete bloodPressure by id
 * @param { object } action - Action with payload data
 */
export function* deleteBloodPressureById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(
      bloodPressureService.deleteBloodPressureById,
      id
    );
    yield put(
      setResponse(
        'success',
        'deleted bloodPressure',
        'patientRecords.recordDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Declare all component sagas
 */
export function* watchBloodPressureSaga() {
  yield takeLatest(GET_ALL_BLOOD_PRESSURES, getBloodPressuresArray);
  yield takeLatest(GET_BLOOD_PRESSURE_BY_ID, getBloodPressureById);
  yield takeLatest(
    GET_BLOOD_PRESSURE_BY_PATIENT_ID,
    getBloodPressureByPatientId
  );
  yield takeLatest(CREATE_BLOOD_PRESSURE, createBloodPressure);
  yield takeLatest(UPDATE_BLOOD_PRESSURE, updateBloodPressure);
  yield takeLatest(DELETE_BLOOD_PRESSURE_BY_ID, deleteBloodPressureById);
}
